import { useEffect, useState, useRef } from "react";
import { Popper, Paper, ClickAwayListener, Typography, Badge } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/NotificationsNoneOutlined";
import styles from "./NotificationPopup.module.scss";

import { KEYS } from "@/src/const";
import TrackingLogic from "@/src/logic/TrackingLogic";
import { FrontendNotificationService } from "@/src/service/flow";
import { NotificationType } from "@/src/type";

import theme from "@/styles/scss/theme.module.scss";

interface NotificationPopupProps {
  isDark?: boolean;
}

function NotificationPopup({ isDark }: NotificationPopupProps) {
  const [notifications, setNotifications] = useState<NotificationType.Notification[]>([]);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    //check for notification that a lead response has been sent by JARVIS AI
    async function getNotifications(autoOpen?: boolean) {
      const sessionId = await TrackingLogic.getSessionId();
      const getNotification = await FrontendNotificationService.getNotificaton("sessionId", sessionId);
      if (getNotification?.response?.payload?.[0]?.refId) {
        setNotifications(getNotification.response.payload);
        if (autoOpen) {
          setOpen(true);
        }
        return true;
      }
      return false;
    }

    (async () => {
      const hasNotification = await getNotifications();
      if (!hasNotification) {
        const interval = setInterval(async () => {
          const notification = localStorage.getItem(KEYS.LOCAL_STORAGE.NOTIFICATION);
          if (notification) {
            const notificationCounter = localStorage.getItem(KEYS.LOCAL_STORAGE.NOTIFICATION_COUNTER);
            const parsedCounter = parseInt(notificationCounter ?? "0");
            const counter = typeof parsedCounter === "number" ? parsedCounter : 0;
            localStorage.setItem(KEYS.LOCAL_STORAGE.NOTIFICATION_COUNTER, `${counter + 1}`);
            if (counter > 100) {
              try {
                localStorage.removeItem(KEYS.LOCAL_STORAGE.NOTIFICATION);
                localStorage.remoteItem(KEYS.LOCAL_STORAGE.NOTIFICATION_COUNTER);
              } catch (err) {}
              clearInterval(interval);
              return;
            }

            const nowHasNotification = await getNotifications(true);
            if (nowHasNotification) {
              clearInterval(interval);
              localStorage.removeItem(KEYS.LOCAL_STORAGE.NOTIFICATION);
            }
          }
        }, 5000);
      }
    })();
  }, []);

  const handleClick = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <>
        <div
          ref={anchorRef}
          onClick={handleClick}
          className={styles.notificationIcon}
        >
          <Badge
            badgeContent={(notifications ?? []).length}
            color={isDark ? "primary" : "secondary"}
            invisible={(notifications ?? []).length === 0}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiBadge-badge": {
                top: 4,
                right: 4,
                color: isDark ? theme.secondaryMain : theme.primaryMain,
              },
            }}
          >
            <NotificationsIcon fontSize="inherit" />
          </Badge>
        </div>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom"
          modifiers={[
            {
              name: "arrow",
              enabled: false,
              options: {
                element: "#notification-arrow",
              },
            },
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ]}
          sx={{
            zIndex: 500,
          }}
        >
          <Paper className={styles.notificationPaper}>
            <div
              id="notification-arrow"
              className={styles.arrow}
            />
            {(notifications ?? []).map((opt, i) => (
              <a
                key={i}
                href={opt?.templateProperties?.action?.href ?? "#"}
                className={styles.notificationContent}
              >
                <Typography variant="h5">{opt?.templateProperties?.text?.[0]?.text}</Typography>
                <Typography variant="body2">{opt?.templateProperties?.text?.[1]?.text}</Typography>
              </a>
            ))}
            {(notifications ?? []).length === 0 && (
              <div className={styles.notificationContent}>
                <Typography variant="h5">No messages</Typography>
                <Typography variant="body2">We will notify you when you have a new message.</Typography>
              </div>
            )}
          </Paper>
        </Popper>
      </>
    </ClickAwayListener>
  );
}

export default NotificationPopup;
